import { Modal, Form, Select, Input, Button, Tag, Typography, Alert } from "antd";
import axios from "axios";
import { useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import {
  CLAIM_STATUS_TAG_COLORS,
  CLAIM_STATUS_REQUIRING_REASON,
  REWARD_CLAIM_STATUS_ENUM,
  formatEnumLabel,
  getAllowedNextClaimStatuses
} from "../helper";

const { Item: FormItem } = Form;
const { TextArea } = Input;
const { Text } = Typography;

/**
 * Modal for updating a reward-kit claim's status, or unblocking it. Offers
 * the transitions allowed from the claim's current status, plus a dedicated
 * Unblock action when the claim is currently BLOCKED (a separate endpoint,
 * never the generic status update). Rejecting or blocking requires a reason.
 *
 * @param {object} props
 * @param {boolean} [props.isModalOpen=false]
 * @param {function} [props.setIsModalOpen] - Receives `false` to close the modal.
 * @param {object} [props.claim] - The claim row being updated.
 * @param {string} props.ROOT_URL - Backend origin for the selected environment.
 * @param {string} props.BASE_URL - Path of the claims admin API under `ROOT_URL`.
 * @param {function} [props.onStatusUpdated] - Called after a successful update so the list refreshes.
 */
const UpdateStatusModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  claim = {},
  ROOT_URL,
  BASE_URL,
  onStatusUpdated = () => {}
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedNewStatus = Form.useWatch("newStatus", form);

  const { status: claimStatus, uniquePublicId, courseName, shippingDetails } = claim;
  const { fullName: userFullName, email: userEmail } = shippingDetails || {};
  const isClaimBlocked = claimStatus === REWARD_CLAIM_STATUS_ENUM.BLOCKED;
  const allowedNextStatuses = getAllowedNextClaimStatuses(claimStatus);
  const requiresReason = CLAIM_STATUS_REQUIRING_REASON.includes(selectedNewStatus);

  /** Closes the modal. */
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  /**
   * Shared submit scaffolding for both claim actions below: flips the loading
   * state, posts to the given endpoint, notifies + refreshes + closes the
   * modal on success, and always resets the loading state at the end.
   *
   * @param {string} url - Full endpoint URL.
   * @param {object} payload - Request body.
   * @param {string} successMessage - Notification shown on success.
   */
  const submitClaimAction = (url, payload, successMessage) => {
    setIsSubmitting(true);
    axios
      .post(url, payload)
      .then(() => {
        showSuccessNotification(successMessage);
        onStatusUpdated();
        setIsModalOpen(false);
      })
      .catch((error) => {
        showErrorNotification(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  /**
   * Posts the chosen status. `adminActionReason` is trimmed, and sent as an empty string when
   * the form did not ask for one.
   *
   * @param {{newStatus: string, adminActionReason?: string}} formValues
   */
  const handleSubmitStatusUpdate = ({ newStatus, adminActionReason = "" }) => {
    submitClaimAction(
      `${ROOT_URL}/${BASE_URL}/update-status`,
      {
        rewardClaimId: uniquePublicId,
        newStatus,
        adminActionReason: adminActionReason.trim()
      },
      "Claim status updated successfully"
    );
  };

  /** Moves a BLOCKED claim to REJECTED through the unblock endpoint. */
  const handleUnblockClaim = () => {
    submitClaimAction(
      `${ROOT_URL}/${BASE_URL}/unblock`,
      { rewardClaimId: uniquePublicId },
      "Claim unblocked successfully"
    );
  };

  return (
    <Modal title="Update Claim Status" open={isModalOpen} onCancel={handleCancel} footer={null}>
      <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: "4px" }}>
        {userFullName && (
          <div>
            <Text strong>User: </Text>
            <Text>{userFullName} ({userEmail})</Text>
          </div>
        )}
        {courseName && (
          <div>
            <Text strong>Course: </Text>
            <Text>{courseName}</Text>
          </div>
        )}
        <div>
          <Text strong>Current Status: </Text>
          <Tag color={CLAIM_STATUS_TAG_COLORS[claimStatus]}>{formatEnumLabel(claimStatus || "")}</Tag>
        </div>
      </div>

      {isClaimBlocked && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This claim is BLOCKED."
            description="Only the Unblock action is available. Unblocking moves the claim to REJECTED so the user can submit it again."
          />
          <Button type="primary" danger loading={isSubmitting} onClick={handleUnblockClaim}>
            Unblock Claim
          </Button>
        </>
      )}

      {!isClaimBlocked && allowedNextStatuses.length === 0 && (
        <Alert type="info" showIcon message="This claim's status is terminal. No further transitions are available." />
      )}

      {!isClaimBlocked && allowedNextStatuses.length > 0 && (
        <Form layout="vertical" form={form} onFinish={handleSubmitStatusUpdate}>
          <FormItem
            name="newStatus"
            label="New Status"
            rules={[{ required: true, message: "Please select a new status" }]}
          >
            <Select
              placeholder="Select new status"
              options={allowedNextStatuses.map((status) => ({
                value: status,
                label: formatEnumLabel(status)
              }))}
            />
          </FormItem>

          {selectedNewStatus === REWARD_CLAIM_STATUS_ENUM.DELIVERED && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Delivering a claim is permanent and cannot be undone."
            />
          )}

          {requiresReason && (
            <FormItem
              name="adminActionReason"
              label="Admin Action Reason"
              rules={[
                { required: true, whitespace: true, message: "A reason is required to reject or block a claim" }
              ]}
            >
              <TextArea rows={3} placeholder="Explain why this claim is being rejected/blocked" />
            </FormItem>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Apply
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default UpdateStatusModal;
